import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, List, Modal, Space, Spin, Typography } from 'antd';
import { EyeOutlined, FileOutlined, PaperClipOutlined } from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { uri } from '../../assets/utils/http-request';
import DashboardSection from '../dashboard/components/DashboardSection';

/**
 * Resolve a browser-openable URL for a PA header / line attachment.
 * Prefers API `url` / `file_url` / `doc_urls`; falls back to api base + relative path.
 */
export function resolvePaAttachmentUrl(att) {
  if (!att) return null;
  if (typeof att === 'string') {
    return resolveRelativeDocUrl(att);
  }
  if (typeof att !== 'object') return null;

  const candidates = [
    att.url,
    att.file_url,
    Array.isArray(att.doc_urls) ? att.doc_urls[0] : null,
    Array.isArray(att.urls) ? att.urls[0] : null,
    att.file_path,
    Array.isArray(att.files) ? att.files[0] : null,
    Array.isArray(att.doc_files) ? att.doc_files[0] : null,
  ].filter((v) => typeof v === 'string' && v.trim());

  for (const raw of candidates) {
    const resolved = resolveRelativeDocUrl(raw);
    if (resolved) return resolved;
  }
  return null;
}

function resolveRelativeDocUrl(raw) {
  const path = String(raw || '').trim();
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  let relative = path.replace(/^\//, '');
  const docsMatch = relative.match(/(?:^|\/)(documents\/.+)$/i);
  if (docsMatch) relative = docsMatch[1];
  const base = uri.endsWith('/') ? uri : `${uri}/`;
  return `${base}${relative}`;
}

function isImageAttachment(att, url) {
  const name = String(
    att?.original_name || att?.file_path || url || '',
  ).toLowerCase();
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(name);
}

function isPdfAttachment(att, url) {
  const name = String(
    att?.original_name || att?.file_path || url || '',
  ).toLowerCase();
  return /\.pdf$/i.test(name);
}

function attachmentLabel(att, idx) {
  if (typeof att === 'string') {
    return att.split('/').pop() || `Document ${idx + 1}`;
  }
  return (
    att?.original_name ||
    att?.file_path?.split('/')?.pop() ||
    (Array.isArray(att?.files) && att.files[0]?.split('/')?.pop()) ||
    `Document ${idx + 1}`
  );
}

/**
 * Read-only supporting documents on admin claim detail.
 * Uses pa_header_attachments from PAListAdminClaim when present; otherwise
 * loads GET /v2/pa/{checkin_id}/evidence-policy (same source as facility).
 */
export default function ClaimDocumentsPanel({
  checkinId,
  attachments: attachmentsProp = null,
  lineItems = [],
  embedded = false,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [headerAttachments, setHeaderAttachments] = useState(
    Array.isArray(attachmentsProp) ? attachmentsProp : [],
  );
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (Array.isArray(attachmentsProp)) {
      setHeaderAttachments(attachmentsProp);
    }
  }, [attachmentsProp]);

  useEffect(() => {
    if (!checkinId) return undefined;
    // Skip network fetch when the claim detail payload already included attachments
    // (including an explicit empty list).
    if (Array.isArray(attachmentsProp)) return undefined;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const parsed = lib.parseApiEnvelope(await lib.getPaEvidencePolicy(checkinId));
        if (cancelled) return;
        if (!parsed.ok) {
          setError(parsed.message || 'Unable to load documents');
          setHeaderAttachments([]);
          return;
        }
        const data = parsed.data ?? {};
        const list =
          data.pa_header_attachments ||
          data.evidence?.pa_header_attachments ||
          data.header_files ||
          data.header_attachments ||
          [];
        setHeaderAttachments(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Unable to load documents');
          setHeaderAttachments([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [checkinId, attachmentsProp]);

  const lineDocs = useMemo(() => {
    const out = [];
    (lineItems || []).forEach((item, itemIdx) => {
      const urls = Array.isArray(item?.doc_urls) ? item.doc_urls : [];
      const files = Array.isArray(item?.doc_files)
        ? item.doc_files
        : String(item?.doc || '')
            .split('|')
            .map((s) => s.trim())
            .filter(Boolean);
      const entries = urls.length
        ? urls.map((url, i) => ({ url, file: files[i] || url, item, itemIdx }))
        : files.map((file) => ({
            url: resolveRelativeDocUrl(file),
            file,
            item,
            itemIdx,
          }));
      entries.forEach((entry, fileIdx) => {
        if (!entry.url && !entry.file) return;
        out.push({
          ...entry,
          key: `line-${item?.id_ || itemIdx}-${fileIdx}`,
          label:
            entry.file?.split('/')?.pop() ||
            `Line ${itemIdx + 1} document ${fileIdx + 1}`,
        });
      });
    });
    return out;
  }, [lineItems]);

  const openAttachment = (att, fallbackUrl = null) => {
    const url = fallbackUrl || resolvePaAttachmentUrl(att);
    if (!url) {
      setError('No preview URL available for this document.');
      return;
    }
    const title = attachmentLabel(att, 0);
    if (isImageAttachment(att, url)) {
      setPreview({ kind: 'image', url, title });
      return;
    }
    if (isPdfAttachment(att, url)) {
      setPreview({ kind: 'pdf', url, title });
      return;
    }
    // Office docs and other types have no reliable in-browser viewer: open in a new tab.
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const hasHeader = headerAttachments.length > 0;
  const hasLine = lineDocs.length > 0;

  if (!checkinId && !hasHeader && !hasLine) {
    return null;
  }

  const body = (
    <>
        {loading ? (
          <div style={{ padding: '12px 0' }}>
            <Spin size="small" />{' '}
            <Typography.Text type="secondary">Loading documents…</Typography.Text>
          </div>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {error ? (
              <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />
            ) : null}

            {!hasHeader && !hasLine ? (
              <Alert
                type="info"
                showIcon
                message="No supporting documents"
                description="No encounter or line documents were attached for this claim."
              />
            ) : null}

            {hasHeader ? (
              <div>
                <Typography.Text strong>Encounter documents</Typography.Text>
                <List
                  size="small"
                  style={{ marginTop: 8 }}
                  dataSource={headerAttachments}
                  renderItem={(att, idx) => {
                    const label = attachmentLabel(att, idx);
                    const url = resolvePaAttachmentUrl(att);
                    return (
                      <List.Item
                        actions={
                          url
                            ? [
                                <Button
                                  key="preview"
                                  type="link"
                                  size="small"
                                  icon={<EyeOutlined />}
                                  onClick={() => openAttachment(att, url)}
                                >
                                  {isImageAttachment(att, url) || isPdfAttachment(att, url)
                                    ? 'Preview'
                                    : 'Open'}
                                </Button>,
                              ]
                            : undefined
                        }
                      >
                        <Space>
                          <FileOutlined />
                          {url ? (
                            <Typography.Link onClick={() => openAttachment(att, url)}>
                              {label}
                            </Typography.Link>
                          ) : (
                            <Typography.Text>{label}</Typography.Text>
                          )}
                        </Space>
                      </List.Item>
                    );
                  }}
                />
              </div>
            ) : null}

            {hasLine ? (
              <div>
                <Typography.Text strong>Line item documents</Typography.Text>
                <List
                  size="small"
                  style={{ marginTop: 8 }}
                  dataSource={lineDocs}
                  renderItem={(doc) => (
                    <List.Item
                      actions={
                        doc.url
                          ? [
                              <Button
                                key="preview"
                                type="link"
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => openAttachment({ file_path: doc.file }, doc.url)}
                              >
                                Open
                              </Button>,
                            ]
                          : undefined
                      }
                    >
                      <Space>
                        <FileOutlined />
                        {doc.url ? (
                          <Typography.Link
                            onClick={() => openAttachment({ file_path: doc.file }, doc.url)}
                          >
                            {doc.label}
                          </Typography.Link>
                        ) : (
                          <Typography.Text>{doc.label}</Typography.Text>
                        )}
                      </Space>
                    </List.Item>
                  )}
                />
              </div>
            ) : null}
          </Space>
        )}
    </>
  );

  return (
    <>
      {embedded ? (
        body
      ) : (
        <DashboardSection
          id="claim-supporting-documents"
          icon={<PaperClipOutlined />}
          title="Supporting documents"
          subtitle="Encounter and line evidence attached for this claim"
          collapsible={false}
          variant="content"
        >
          {body}
        </DashboardSection>
      )}

      <Modal
        open={Boolean(preview)}
        title={preview?.title || 'Document preview'}
        footer={
          preview?.url
            ? [
                <Button key="open" type="link" href={preview.url} target="_blank" rel="noreferrer">
                  Open in new tab
                </Button>,
                <Button key="close" onClick={() => setPreview(null)}>
                  Close
                </Button>,
              ]
            : [
                <Button key="close" onClick={() => setPreview(null)}>
                  Close
                </Button>,
              ]
        }
        onCancel={() => setPreview(null)}
        width={preview?.kind === 'pdf' ? 860 : 720}
        destroyOnClose
      >
        {preview?.kind === 'image' && preview?.url ? (
          <img
            src={preview.url}
            alt={preview.title || 'Attachment'}
            style={{ maxWidth: '100%', display: 'block', margin: '0 auto' }}
          />
        ) : null}
        {preview?.kind === 'pdf' && preview?.url ? (
          <>
            <iframe
              src={preview.url}
              title={preview.title || 'Document preview'}
              style={{ width: '100%', height: '70vh', border: 'none', display: 'block' }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
              Preview not loading? Use "Open in new tab" below.
            </Typography.Text>
          </>
        ) : null}
      </Modal>
    </>
  );
}
